import React, { useState, useEffect, useRef, useMemo, useContext } from "react";
import { CreateUseCaseContext } from "../../../../store";
import { GlobalContext } from "../../../../store";
import ChatBox from "../chat-box/chat-box.component";
import ChatInput from "../chat-input/chat-input.component";
import ChatSelect from "../chat-select/chat-select.component";
import HeaderComponent from "../../../../components/header/header.component";
import CasePreviewModal from "../../../../dialogs/case-preview/case-preview.component";
import { ConfirmDialog } from "../../../../dialogs/confirm-dialog/confirm-dialog";
import { TypingProvider, useTyping } from "../../context/typing-context";
import { AiQuestion, UserAnswer } from "../../../../types/ai-question";
import { ChatMessage } from "../../../../types/chat-message";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@material-ui/core";
import "./chatbot.component.scoped.scss";
import ProgressIndicator from "../progress-indicator/progress-indicator.component";
import ActionButtons from "../action-buttons/action-buttons.component";
import ModalQuestionHandler from "../modal-question-handler/modal-question-handler.component";
import {
  getActiveQuestion,
  questionRequiresFile,
  isInputDisabled,
  findQuestionById,
  updateUserAnswer,
  createUserMessage,
  createAIQuestionMessage,
  createRepeatMessage,
  createConfirmationMessage,
  createCompletionMessage,
  createSubmissionMessage,
  getNextQuestion,
  isRepeatedQuestion,
  extractPayloadFromAnswers,
  isSelectAIQuestion,
  getSelectQuestionFromMessage,
  isMessageDisabled,
  isMessageFocused,
  isAnotherMessageFocused,
  isMessageRepeatedQuestion,
  extractAttachmentsFromAnswers,
  uploadFiles,
} from "../../../../helpers/chatbotUtils";
import { CasesService } from "../../../../services/cases/cases.service";
import { history } from "react-router-guard";
import { CASES_PAGE_TYPES } from "../../../case/types/AI-roles";
import { UserRoles } from "../../../../types/userRoles";
const AI_ASSISTANT_NAME = "AI Assistant";

// Inner component that uses the typing context
const ChatbotContent = () => {
  const { t, ready, i18n } = useTranslation();
  const { user } = useContext(GlobalContext);
  const {
    isTypingComplete,
    currentMessageId,
    setIsTypingComplete,
  } = useTyping();
  const conversationRef = useRef<HTMLDivElement>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [showExitConfirmModal, setShowExitConfirmModal] = useState(false);
  const {
    useCase,
    riskLevels,
    products,
    caseRoles,
    lands,
    branches,
    areas,
    operations,
    algorithms,
    techniques,
    machineLearningTypes,
    role,
  } = useContext(CreateUseCaseContext);
  // Make questions reactive to language changes
  const QUESTIONS: AiQuestion[] = useMemo(
    () => [
      {
        id: 1,
        question: t("CHATBOT.QUESTION_1"),
        type: "text",
        field: { key: "title", name: "Title" },
        requiresAnswer: true,
        requiresFile: false,
        isShown: true,
      },
      {
        id: 2,
        question: t("CHATBOT.QUESTION_2"),
        type: "text",
        field: { key: "brief", name: "Brief" },
        requiresAnswer: true,
        requiresFile: false,
        isShown: true,
      },
      {
        id: 3,
        question: t("CHATBOT.QUESTION_3"),
        type: "select",
        field: { key: "branchId", name: "Branch", isMultiple: false },
        options: (branches || []).map((branch: any) => ({
          id: branch.id,
          name: branch.name,
        })),
        requiresAnswer: true,
        requiresFile: false,
        isShown: true,
      },
      {
        id: 4,
        question: t("CHATBOT.QUESTION_4"),
        type: "select",
        field: { key: "landId", name: "Country", isMultiple: false },
        options: (lands || []).map((land: any) => ({
          id: land.id,
          name: land.name,
        })),
        requiresAnswer: true,
        requiresFile: false,
        isShown: true,
      },
      {
        id: 5,
        question: t("CHATBOT.QUESTION_5"),
        type: "text",
        hasFile: true,
        field: { key: "description", name: "Description" },
        requiresAnswer: true,
        requiresFile: true,
        isShown: true,
      },

      {
        id: 6,
        question: t("CHATBOT.QUESTION_6"),
        type: "select",
        field: { key: "riskLevelId", name: "AI Risk Level", isMultiple: false },
        options: (riskLevels || []).map((riskLevel: any) => ({
          id: riskLevel.id,
          name: riskLevel.name,
          iconKey: riskLevel.iconKey,
          description: riskLevel.description,
        })),
        requiresAnswer: true,
        requiresFile: false,
        isShown: true,
      },

      {
        id: 7,
        question: t("CHATBOT.QUESTION_7"),
        type: "select",
        field: { key: "roleId", name: "UseCase Role", isMultiple: false },
        options: (caseRoles || []).map((role: any) => ({
          id: role.id,
          name: role.name,
        })),
        requiresAnswer: true,
        requiresFile: false,
        isShown: true,
      },

      {
        id: 8,
        question: t("CHATBOT.QUESTION_8"),
        type: "select",
        field: { key: "productId", name: "AI Product", isMultiple: false },
        options: (products || []).map((product: any) => ({
          id: product.id,
          name: product.name,
        })),
        requiresAnswer: true,
        requiresFile: false,
        isShown: true,
      },

      {
        id: 9,
        question: t("CHATBOT.QUESTION_9"),
        type: "text",
        field: { key: "scenarioDescription", name: "Additional Information" },
        requiresAnswer: true,
        requiresFile: false,
        isShown: true,
      },

      {
        id: 10,
        question: t("CHATBOT.QUESTION_10"),
        type: "select",
        field: {
          key: "operationsIds",
          name: "AI Operation Area",
          isMultiple: true,
        },
        options: (operations || []).map((operation: any) => ({
          id: operation.id,
          name: operation.name,
          iconKey: operation.iconKey,
          description: operation.description,
        })),
        requiresAnswer: true,
        requiresFile: false,
        isShown: true,
      },

      {
        id: 11,
        question: t("CHATBOT.QUESTION_11"),
        type: "select",
        field: {
          key: "areasIds",
          name: "AI Application Areas",
          isMultiple: true,
        },
        options: (areas || []).map((area: any) => ({
          id: area.id,
          name: area.name,
          iconKey: area.iconKey,
          description: area.description,
        })),
        requiresAnswer: true,
        requiresFile: false,
        isShown: true,
      },
      {
        id: 12,
        question: t("CHATBOT.QUESTION_12"),
        type: "select",
        field: {
          key: "techniquesIds",
          name: "AI Techniques",
          isMultiple: true,
        },
        options: (techniques || []).map((technique: any) => ({
          id: technique.id,
          name: technique.name,
        })),
        requiresAnswer: true,
        requiresFile: false,
        isShown: true,
      },
      {
        id: 13,
        question: t("CHATBOT.QUESTION_13"),
        type: "select",
        field: {
          key: "machineLearningIds",
          name: "Machine Learning Types",
          isMultiple: true,
        },
        options: (machineLearningTypes || []).map(
          (machineLearningType: any) => ({
            id: machineLearningType.id,
            name: machineLearningType.name,
          })
        ),
        requiresAnswer: true,
        requiresFile: false,
        isShown: true,
      },
      {
        id: 14,
        question: t("CHATBOT.QUESTION_14"),
        type: "select",
        field: { key: "algorithmsIds", name: "AI algorithm", isMultiple: true },
        options: (algorithms || []).map((algorithm: any) => ({
          id: algorithm.id,
          name: algorithm.name,
          iconKey: algorithm.iconKey,
          description: algorithm.description,
        })),
        requiresAnswer: true,
        requiresFile: false,
        isShown: true,
      },
      {
        id: 15,
        question: t("CHATBOT.QUESTION_15"),
        type: "modal",
        field: { key: "users", name: "Assigned Users" },
        requiresAnswer: true,
        requiresFile: false,
        isShown: UserRoles.FULL_ACCESS_ROLES.includes(user.role),
      },
    ],
    [
      t,
      riskLevels,
      products,
      caseRoles,
      branches,
      lands,
      operations,
      areas,
      algorithms,
      techniques,
      machineLearningTypes,
      user,
    ]
  );

  // Filter questions to only include those that should be shown
  const SHOWN_QUESTIONS = useMemo(
    () => QUESTIONS.filter((question) => question.isShown),
    [QUESTIONS]
  );

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<UserAnswer[]>([]);
  const [conversationHistory, setConversationHistory] = useState<ChatMessage[]>(
    []
  );
  const [isWaitingForAnswer, setIsWaitingForAnswer] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [selectedAnswers, setSelectedAnswers] = useState<{
    [key: string]: string[];
  }>({});

  // New state to track which question is being edited
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(
    null
  );

  // State to trigger auto focus on input field
  const [shouldAutoFocus, setShouldAutoFocus] = useState(false);

  // New state to track which question is currently being repeated (for focus mode)
  const [focusedQuestionId, setFocusedQuestionId] = useState<string | null>(
    null
  );

  // New state to track the original current question when repeating
  const [originalCurrentQuestionId, setOriginalCurrentQuestionId] = useState<
    string | null
  >(null);

  // New state to track a pending next question after confirmation
  const [pendingNextQuestion, setPendingNextQuestion] = useState<
    { origQ?: typeof SHOWN_QUESTIONS[number] } | undefined
  >(undefined);

  // New state to track if the case is being saved
  const [saving, setSaving] = useState(false);

  // New state to track if case was created successfully
  const [caseCreatedSuccessfully, setCaseCreatedSuccessfully] = useState(false);

  // Helper function to check if a question is being edited
  const isQuestionBeingEdited = (questionId: string) => {
    return editingQuestionId === questionId;
  };

  // Helper function to check if a user message belongs to the focused question
  const isUserMessageForFocusedQuestion = (
    message: ChatMessage,
    focusedQuestionId: string
  ) => {
    if (!message.isUserMessage) return false;

    // Find the index of this message in the conversation history
    const messageIndex = conversationHistory.findIndex(
      (msg) => msg.id === message.id
    );
    if (messageIndex === -1) return false;

    // Look backwards to find the most recent AI question before this user message
    for (let i = messageIndex - 1; i >= 0; i--) {
      const prevMessage = conversationHistory[i];
      if (!prevMessage.isUserMessage && prevMessage.questionId) {
        // Check if this AI question matches the focused question
        return prevMessage.questionId === focusedQuestionId;
      }
    }

    return false;
  };

  // Helper function to create confirmation messages (moved here for correct usage)
  const getConfirmationMessage = (
    editedQuestionType: "text" | "select",
    hasOriginalQuestion: boolean
  ) => {
    if (editedQuestionType === "select") {
      return t("CHATBOT.UPDATED_BACK_TO_PREVIOUS");
    } else {
      return hasOriginalQuestion
        ? t("CHATBOT.UPDATED_PROCEED_NEXT")
        : t("CHATBOT.UPDATED_BACK_TO_PREVIOUS");
    }
  };

  // Auto-scroll to bottom when conversation updates or options become visible
  useEffect(() => {
    if (conversationRef.current) {
      // Add a small delay to ensure the message is fully rendered
      setTimeout(() => {
        if (conversationRef.current) {
          conversationRef.current.scrollTop =
            conversationRef.current.scrollHeight;
        }
      }, 100);
    }
  }, [conversationHistory, isTypingComplete]);

  // Initialize with the welcome message
  useEffect(() => {
    if (
      ready &&
      user?.firstName &&
      SHOWN_QUESTIONS.length > 0 &&
      conversationHistory.length === 0
    ) {
      const username = user.firstName + " " + user.lastName;
      const welcomeMessage = t("CHATBOT.WELCOME_MESSAGE", { username });
      setConversationHistory([
        {
          id: `welcome-message`,
          authorName: AI_ASSISTANT_NAME,
          isUserMessage: false,
          messageContent: welcomeMessage,
        },
      ]);
      setIsWaitingForAnswer(false);
      setShouldAutoFocus(false);
    }
  }, [ready, SHOWN_QUESTIONS, conversationHistory.length, t, user]);

  // After welcome message is typed, show the first question
  useEffect(() => {
    if (
      conversationHistory.length === 1 &&
      conversationHistory[0].id === "welcome-message" &&
      isTypingComplete
    ) {
      const firstQuestion = SHOWN_QUESTIONS[0];
      setConversationHistory((prev) => [
        ...prev,
        {
          id: `question-${firstQuestion.id}`,
          authorName: AI_ASSISTANT_NAME,
          isUserMessage: false,
          messageContent: firstQuestion.question,
          questionId: `q${firstQuestion.id}`,
        },
      ]);
      setIsWaitingForAnswer(true);
      setShouldAutoFocus(true);
    }
  }, [conversationHistory, isTypingComplete, SHOWN_QUESTIONS]);

  // Effect to show the next question after confirmation message finishes typing
  useEffect(() => {
    if (!pendingNextQuestion) return;
    // Find the last message in the conversation
    const lastMessage = conversationHistory[conversationHistory.length - 1];
    // Only proceed if the last message is a confirmation and typing is complete
    if (
      lastMessage &&
      lastMessage.id.startsWith("confirmation-") &&
      isTypingComplete
    ) {
      if (pendingNextQuestion.origQ) {
        // Show the original question again
        setConversationHistory((prev) => [
          ...prev,
          {
            id: `question-${pendingNextQuestion.origQ?.id}-${Date.now()}`,
            authorName: AI_ASSISTANT_NAME,
            isUserMessage: false,
            messageContent: pendingNextQuestion.origQ?.question ?? "",
            questionId: pendingNextQuestion.origQ
              ? `q${pendingNextQuestion.origQ.id}`
              : undefined,
          },
        ]);
        setIsWaitingForAnswer(true);
      } else {
        // Check if there's a current select question waiting for an answer
        const currentQ = SHOWN_QUESTIONS[currentQuestionIndex];
        if (
          currentQ &&
          currentQ.type === "select" &&
          !isQuestionAnswered(`q${currentQ.id}`)
        ) {
          // Don't proceed to next question, wait for user to answer current select question
          setPendingNextQuestion(undefined);
          return;
        }

        // Proceed to the next question in the flow
        const nextQuestionIndex = currentQuestionIndex + 1;
        if (nextQuestionIndex < SHOWN_QUESTIONS.length) {
          const nextQuestion = SHOWN_QUESTIONS[nextQuestionIndex];
          setConversationHistory((prev) => [
            ...prev,
            {
              id: `question-${nextQuestion.id}`,
              authorName: AI_ASSISTANT_NAME,
              isUserMessage: false,
              messageContent: nextQuestion.question,
              questionId: `q${nextQuestion.id}`,
            },
          ]);
          setCurrentQuestionIndex(nextQuestionIndex);
          setIsWaitingForAnswer(true);
        } else {
          // All questions answered - show completion message
          const completionMessage = {
            id: `completion-${Date.now()}`,
            authorName: AI_ASSISTANT_NAME,
            isUserMessage: false,
            messageContent: t("CHATBOT.THANK_YOU_FOR_ANSWERING_ALL_QUESTIONS"),
          };
          setConversationHistory((prev) => [...prev, completionMessage]);
          setIsCompleted(true);
        }
      }
      setPendingNextQuestion(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pendingNextQuestion,
    conversationHistory,
    isTypingComplete,
    currentQuestionIndex,
    SHOWN_QUESTIONS,
  ]);

  // Helper function to check if a question has been answered
  const isQuestionAnswered = (questionId: string) => {
    // If question is being edited, it's not considered answered
    if (isQuestionBeingEdited(questionId)) {
      return false;
    }

    const question = findQuestionById(SHOWN_QUESTIONS, questionId);

    // For informational messages that don't require answers, consider them answered
    if (question && question.requiresAnswer === false) {
      return true;
    }

    // For single field questions, use existing logic
    return userAnswers.some((answer) => answer.questionId === question?.id);
  };

  const handleSendMessage = (message: string, attachment?: File | File[]) => {
    // Extract file attachment from the message
    let cleanMessage = message;

    if (attachment) {
      // Check if message contains file attachment indicators
      const fileRegex = /📎 Attached: (.+?) \((\d+\.\d+) MB\)/g;
      let match;
      const matches: RegExpExecArray[] = [];

      while ((match = fileRegex.exec(message)) !== null) {
        matches.push(match);
      }

      if (matches.length > 0) {
        // Remove file attachment indicators from the message
        cleanMessage = message.replace(fileRegex, "").trim();
      }
    }

    // Add user message to conversation
    setConversationHistory((prev) => [
      ...prev,
      createUserMessage(message, attachment),
    ]);

    // Check if this is a repeated question being answered
    // Look for the last AI message to see if it's a repeat
    const lastAIMessage = conversationHistory
      .filter((msg) => !msg.isUserMessage)
      .pop();

    const repeated = isRepeatedQuestion(lastAIMessage?.id);

    // Determine which question this answer belongs to
    let questionToAnswer: typeof SHOWN_QUESTIONS[number];
    if (repeated && lastAIMessage?.questionId) {
      questionToAnswer =
        findQuestionById(SHOWN_QUESTIONS, lastAIMessage.questionId) ||
        SHOWN_QUESTIONS[currentQuestionIndex];
    } else {
      questionToAnswer = SHOWN_QUESTIONS[currentQuestionIndex];
    }

    // Save user answer
    const userAnswer: UserAnswer = {
      questionId: questionToAnswer.id,
      question: questionToAnswer.question,
      type: questionToAnswer.type,
      answer: cleanMessage, // Save clean message without file indicators
      timestamp: new Date(),
      attachment: attachment,
    };

    setUserAnswers((prev) => updateUserAnswer(prev, userAnswer));
    setIsWaitingForAnswer(false);

    // Clear focused question if this was a repeated question
    if (repeated) {
      setFocusedQuestionId(null);

      // Check if all required questions are answered
      const requiredQuestionsCount = SHOWN_QUESTIONS.filter(
        (q) => q.requiresAnswer !== false
      ).length;
      const answeredQuestionsCount = userAnswers.length;

      // If all questions are answered, show completion message instead of confirmation
      if (answeredQuestionsCount >= requiredQuestionsCount) {
        setConversationHistory((prev) => [
          ...prev,
          createCompletionMessage(
            t("CHATBOT.THANK_YOU_FOR_ANSWERING_ALL_QUESTIONS")
          ),
        ]);
        setIsCompleted(true);
        return;
      }

      // If we have an original current text question to return to, show it again
      if (originalCurrentQuestionId) {
        const origQ = findQuestionById(
          SHOWN_QUESTIONS,
          originalCurrentQuestionId
        );
        if (origQ && origQ.type === "text") {
          // Insert confirmation message before returning to original question
          setConversationHistory((prev) => [
            ...prev,
            createConfirmationMessage(getConfirmationMessage("text", true)),
          ]);
          setIsTypingComplete(false);
          setPendingNextQuestion({ origQ });
          setIsWaitingForAnswer(false);
          setOriginalCurrentQuestionId(null);
          return; // Don't advance to next question
        }
        setOriginalCurrentQuestionId(null);
      } else {
        // Insert confirmation message before proceeding to next question
        setConversationHistory((prev) => [
          ...prev,
          createConfirmationMessage(getConfirmationMessage("text", false)),
        ]);
        setIsTypingComplete(false);
        setPendingNextQuestion({ origQ: undefined });
        setIsWaitingForAnswer(false);
        return;
      }
    }

    // Only move to next question if this is not a repeated question
    if (!repeated) {
      const { nextQuestion, hasNext } = getNextQuestion(
        SHOWN_QUESTIONS,
        currentQuestionIndex
      );
      if (hasNext) {
        setConversationHistory((prev) => [
          ...prev,
          createAIQuestionMessage(nextQuestion),
        ]);
        setCurrentQuestionIndex(currentQuestionIndex + 1);
        setIsWaitingForAnswer(true);

        // Trigger auto focus for text questions
        if (nextQuestion.type === "text") {
          setShouldAutoFocus(true);
        }
      } else {
        setConversationHistory((prev) => [
          ...prev,
          createCompletionMessage(
            t("CHATBOT.THANK_YOU_FOR_ANSWERING_ALL_QUESTIONS")
          ),
        ]);
        setIsCompleted(true);
      }
    }
  };

  const handleSelectAnswer = (questionId: string, answers: string[]) => {
    setSelectedAnswers((prev) => ({
      ...prev,
      [questionId]: answers,
    }));

    const question = findQuestionById(SHOWN_QUESTIONS, questionId);
    if (!question) return;

    const isEditingRepeatedQuestion = editingQuestionId === questionId;

    if (question.type === "select") {
      const userAnswer: UserAnswer = {
        questionId: question.id,
        question: question.question,
        type: question.type,
        answer: answers, // Store as array, not string
        timestamp: new Date(),
      };
      setUserAnswers((prev) => updateUserAnswer(prev, userAnswer));
      setEditingQuestionId(null);
      setFocusedQuestionId(null);
      setIsWaitingForAnswer(false);

      // If we were completed and this was an edit, don't add any messages to chat
      if (isCompleted) {
        return;
      }

      if (!isEditingRepeatedQuestion) {
        const { nextQuestion, hasNext } = getNextQuestion(
          SHOWN_QUESTIONS,
          currentQuestionIndex
        );
        if (hasNext) {
          setConversationHistory((prev) => [
            ...prev,
            createAIQuestionMessage(nextQuestion),
          ]);
          setCurrentQuestionIndex(currentQuestionIndex + 1);
          setIsWaitingForAnswer(true);

          // Trigger auto focus for text questions
          if (nextQuestion.type === "text") {
            setShouldAutoFocus(true);
          }
        } else {
          setConversationHistory((prev) => [
            ...prev,
            createCompletionMessage(
              t("CHATBOT.THANK_YOU_FOR_ANSWERING_ALL_QUESTIONS")
            ),
          ]);
          setIsCompleted(true);
        }
      }
      return;
    }

    // For non-select questions, add user message to conversation
    setConversationHistory((prev) => [
      ...prev,
      createUserMessage(answers.join(", ")),
    ]);
    const userAnswer: UserAnswer = {
      questionId: question.id,
      question: question.question,
      type: question.type,
      answer: answers.join(", "),
      timestamp: new Date(),
    };
    setUserAnswers((prev) => updateUserAnswer(prev, userAnswer));
    setIsWaitingForAnswer(false);
    const { nextQuestion, hasNext } = getNextQuestion(
      SHOWN_QUESTIONS,
      currentQuestionIndex
    );
    if (hasNext) {
      setConversationHistory((prev) => [
        ...prev,
        createAIQuestionMessage(nextQuestion),
      ]);
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setIsWaitingForAnswer(true);
    } else {
      setConversationHistory((prev) => [
        ...prev,
        createCompletionMessage(
          t("CHATBOT.THANK_YOU_FOR_ANSWERING_ALL_QUESTIONS")
        ),
      ]);
      setIsCompleted(true);
    }
  };

  const handleModalAnswer = (
    questionId: string,
    isFirstTimeSubmit: boolean,
    users: any[]
  ) => {
    const question = findQuestionById(SHOWN_QUESTIONS, questionId);
    if (!question) return;

    const userAnswer: UserAnswer = {
      questionId: question.id,
      question: question.question,
      answer: JSON.stringify(users), // Use the passed users
      type: question.type,
      timestamp: new Date(),
    };
    setUserAnswers((prev) => updateUserAnswer(prev, userAnswer));
    setIsWaitingForAnswer(false);

    // Check if we're editing - if so, don't proceed to next question
    const isEditing = editingQuestionId === questionId;
    if (isEditing && !isFirstTimeSubmit) {
      setEditingQuestionId(null);
      setFocusedQuestionId(null);
      return;
    }

    // Proceed to next question only if not editing
    const { nextQuestion, hasNext } = getNextQuestion(
      SHOWN_QUESTIONS,
      currentQuestionIndex
    );
    if (hasNext) {
      setConversationHistory((prev) => [
        ...prev,
        createAIQuestionMessage(nextQuestion),
      ]);
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setIsWaitingForAnswer(true);

      // Trigger auto focus for text questions
      if (nextQuestion.type === "text") {
        setShouldAutoFocus(true);
      }
    } else {
      setConversationHistory((prev) => [
        ...prev,
        createCompletionMessage(
          t("CHATBOT.THANK_YOU_FOR_ANSWERING_ALL_QUESTIONS")
        ),
      ]);
      setIsCompleted(true);
    }
  };

  const handleModalEdit = (questionId: string) => {
    if (isSubmitted || caseCreatedSuccessfully) return;
    // Set the question as being edited
    setEditingQuestionId(questionId);
    setFocusedQuestionId(questionId);

    // Remove the previous answer for this question
    setUserAnswers((prev) =>
      prev.filter(
        (answer) => answer.questionId !== parseInt(questionId.replace("q", ""))
      )
    );
  };

  const handleRepeatQuestion = (questionId: string) => {
    if (isSubmitted || caseCreatedSuccessfully) return;
    const question = findQuestionById(SHOWN_QUESTIONS, questionId);
    if (!question) return;

    // If we're completed and this is a select question, don't add any messages to chat
    if (isCompleted && question.type === "select") {
      setFocusedQuestionId(questionId);
      setUserAnswers((prev) =>
        prev.filter((answer) => answer.questionId !== question.id)
      );
      setSelectedAnswers((prev) => {
        const newState = { ...prev };
        delete newState[questionId];
        return newState;
      });
      setEditingQuestionId(questionId);
      return;
    }

    setFocusedQuestionId(questionId);
    const currentQ = SHOWN_QUESTIONS[currentQuestionIndex];
    if (
      currentQ &&
      currentQ.type === "text" &&
      question.type === "text" &&
      `q${currentQ.id}` !== questionId
    ) {
      setOriginalCurrentQuestionId(`q${currentQ.id}`);
    } else {
      setOriginalCurrentQuestionId(null);
    }

    if (question.type === "select") {
      setUserAnswers((prev) =>
        prev.filter((answer) => answer.questionId !== question.id)
      );
      setSelectedAnswers((prev) => {
        const newState = { ...prev };
        delete newState[questionId];
        return newState;
      });
      setEditingQuestionId(questionId);
      setIsCompleted(false);
      return;
    }

    // For text questions, add repeat message to conversation
    setConversationHistory((prev) => [...prev, createRepeatMessage(question)]);
    setIsWaitingForAnswer(true);
    setIsCompleted(false);
  };

  const handleSubmit = () => {
    setIsSubmitted(true);
    createCase(userAnswers);
  };

  const handlePreview = () => {
    setPreviewModalOpen(true);
  };

  const handleExitChat = () => {
    // If case was created successfully, navigate directly without showing confirmation modal
    if (caseCreatedSuccessfully) {
      if (history.location.search.includes(CASES_PAGE_TYPES.DEVELPMENT)) {
        history.push("/ai-software-development");
      } else if (history.location.search.includes(CASES_PAGE_TYPES.DEMAND)) {
        history.push("/ai-demand");
      }
      return;
    }
    setShowExitConfirmModal(true);
  };

  const handleExitConfirm = (confirmed: boolean) => {
    setShowExitConfirmModal(false);
    if (confirmed) {
      if (history.location.search.includes(CASES_PAGE_TYPES.DEVELPMENT)) {
        history.push("/ai-software-development");
      } else if (history.location.search.includes(CASES_PAGE_TYPES.DEMAND)) {
        history.push("/ai-demand");
      }
    }
  };

  const createCase = async (answers: UserAnswer[]) => {
    try {
      setSaving(true);
      const payload = extractPayloadFromAnswers(answers, SHOWN_QUESTIONS);
      payload.users = useCase.users;
      if (UserRoles.FULL_ACCESS_ROLES.includes(user.role)) {
        const keyContactUser = payload.users.find(
          (item: any) => item.keyContact
        );
        payload.keyContactUserId = keyContactUser?.id || 0;
      } else {
        payload.keyContactUserId = Number(user.id);
        payload.users = [user];
      }
      const files = extractAttachmentsFromAnswers(answers, SHOWN_QUESTIONS);
      console.log("payload:", payload);
      console.log("files:", files);
      const caseServices = new CasesService();
      const result = await caseServices.create(payload);
      if (files.length) {
        const { caseId, internalId } = result.data;
        await uploadFiles(files, caseId, internalId, caseServices);
      }
      if (result.message === "Success") {
        setConversationHistory((prev) => [
          ...prev,
          createSubmissionMessage(
            t("CHATBOT.YOUR_ANSWERS_HAVE_BEEN_SUBMITTED_SUCCESSFULLY")
          ),
        ]);
        setCaseCreatedSuccessfully(true);
      } else {
        setConversationHistory((prev) => [
          ...prev,
          createSubmissionMessage(t("SOME_THING_WENT_WRONG")),
        ]);
      }
      setSaving(false);
    } catch (error) {
      console.error("Error sending answers to API:", error);
      setConversationHistory((prev) => [
        ...prev,
        createSubmissionMessage(t("SOME_THING_WENT_WRONG")),
      ]);
      setSaving(false);
    }
  };

  // Determine language for ChatMic
  const chatMicLanguage = i18n.language === "DE" ? "de-DE" : "en-US";

  // Show empty state if translations aren't ready
  if (!ready) {
    return (
      <Box className="case-assistant-container">
        <HeaderComponent />
      </Box>
    );
  }

  return (
    <Box className="case-assistant-container">
      <HeaderComponent />

      {/* Progress indicator */}
      <ProgressIndicator
        currentQuestionIndex={currentQuestionIndex}
        totalQuestions={
          SHOWN_QUESTIONS.filter((q) => q.requiresAnswer !== false).length
        }
        isWaitingForAnswer={isWaitingForAnswer}
        answeredQuestionsCount={
          focusedQuestionId
            ? userAnswers.filter((answer) => {
                const questionNumber = parseInt(
                  focusedQuestionId.replace("q", "")
                );
                return answer.questionId !== questionNumber;
              }).length
            : userAnswers.length
        }
      />

      {/* Scrollable conversation area */}
      <div className="conversation-area" ref={conversationRef}>
        {/* Display conversation history with ChatSelects after select questions */}
        {conversationHistory.map((message, idx) => {
          const selectQuestion = getSelectQuestionFromMessage(
            message,
            SHOWN_QUESTIONS
          );
          const isSelectQuestion = isSelectAIQuestion(
            message,
            SHOWN_QUESTIONS,
            isTypingComplete,
            currentMessageId
          );
          return (
            <React.Fragment key={message.id}>
              <ChatBox
                messageId={message.id}
                authorName={message.authorName}
                isUserMessage={message.isUserMessage}
                messageContent={message.messageContent}
                questionId={message.questionId}
                questionType={selectQuestion?.type}
                onRepeatQuestion={handleRepeatQuestion}
                onEditQuestion={handleModalEdit}
                isQuestionAnswered={
                  message.questionId
                    ? isQuestionAnswered(message.questionId)
                    : false
                }
                isDisabled={isMessageDisabled(
                  message,
                  focusedQuestionId,
                  isQuestionBeingEdited,
                  isUserMessageForFocusedQuestion
                )}
                isFocused={isMessageFocused(message, focusedQuestionId)}
                isRepeatedQuestion={isMessageRepeatedQuestion(
                  message,
                  focusedQuestionId
                )}
                isTyping={!isTypingComplete}
                attachment={
                  message.attachment
                    ? Array.isArray(message.attachment)
                      ? message.attachment
                      : [message.attachment]
                    : undefined
                }
                isSubmitted={isSubmitted}
                caseCreatedSuccessfully={caseCreatedSuccessfully}
              />
              {isSelectQuestion && selectQuestion && (
                <Box
                  className="chat-select-section"
                  style={{
                    opacity: isAnotherMessageFocused(message, focusedQuestionId)
                      ? 0.5
                      : 1,
                    pointerEvents: isAnotherMessageFocused(
                      message,
                      focusedQuestionId
                    )
                      ? "none"
                      : "auto",
                    filter: isAnotherMessageFocused(message, focusedQuestionId)
                      ? "blur(1px)"
                      : "none",
                  }}
                >
                  {isQuestionBeingEdited(message.questionId!) && (
                    <Typography
                      variant="body2"
                      style={{ marginBottom: "16px", color: "#666" }}
                    >
                      {t("CHATBOT.EDIT_YOUR_SELECTIONS")}
                    </Typography>
                  )}
                  <ChatSelect
                    questionId={message.questionId!}
                    options={selectQuestion.options || []}
                    isMultiple={selectQuestion.field?.isMultiple || false}
                    onAnswer={(questionId, answers) => {
                      setEditingQuestionId(null);
                      handleSelectAnswer(questionId, answers);
                    }}
                    disabled={
                      isSubmitted ||
                      (isQuestionAnswered(message.questionId!) &&
                        !isQuestionBeingEdited(message.questionId!)) ||
                      (focusedQuestionId !== null &&
                        message.questionId !== focusedQuestionId &&
                        !isQuestionBeingEdited(message.questionId!))
                    }
                    selectedAnswers={selectedAnswers[message.questionId!] || []}
                  />
                </Box>
              )}

              {/* Modal Question Handler */}
              {selectQuestion?.type === "modal" && (
                <Box
                  className="chat-modal-section"
                  style={{
                    opacity: isAnotherMessageFocused(message, focusedQuestionId)
                      ? 0.5
                      : 1,
                    pointerEvents: isAnotherMessageFocused(
                      message,
                      focusedQuestionId
                    )
                      ? "none"
                      : "auto",
                    filter: isAnotherMessageFocused(message, focusedQuestionId)
                      ? "blur(1px)"
                      : "none",
                  }}
                >
                  <ModalQuestionHandler
                    onAnswer={(isFirstTimeSubmit, users) =>
                      handleModalAnswer(
                        message.questionId!,
                        isFirstTimeSubmit,
                        users
                      )
                    }
                    isEditing={isQuestionBeingEdited(message.questionId!)}
                  />
                </Box>
              )}
            </React.Fragment>
          );
        })}

        {/* Action buttons at the end when completed */}
        {isCompleted && isTypingComplete && !isSubmitted && (
          <ActionButtons
            focusedQuestionId={focusedQuestionId}
            onSubmit={handleSubmit}
            onPreview={handlePreview}
            saving={saving}
          />
        )}
      </div>

      {/* Chat input at the bottom */}
      <Box className="chat-input-section">
        <ChatInput
          autoFocus={shouldAutoFocus}
          onSendMessage={handleSendMessage}
          onExitChat={handleExitChat}
          requiresFile={questionRequiresFile(
            getActiveQuestion({
              questions: SHOWN_QUESTIONS,
              currentQuestionIndex,
              focusedQuestionId,
            })
          )}
          disabled={isInputDisabled({
            question: getActiveQuestion({
              questions: SHOWN_QUESTIONS,
              currentQuestionIndex,
              focusedQuestionId,
            }),
            isSubmitted,
            isTypingComplete,
            isQuestionAnswered,
            focusedQuestionId,
            isCompleted,
          })}
          language={chatMicLanguage}
        />
      </Box>

      {/* Preview Modal */}
      {previewModalOpen && (
        <CasePreviewModal
          open={previewModalOpen}
          onClose={() => setPreviewModalOpen(false)}
          userAnswers={userAnswers}
          questions={QUESTIONS}
          role={role}
        />
      )}

      {/* Exit Confirmation Modal */}
      <ConfirmDialog
        show={showExitConfirmModal}
        onClose={handleExitConfirm}
        dialogTitle={t("CHATBOT.EXIT_CHAT_TITLE")}
        dialogBody={t("CHATBOT.EXIT_CHAT_MESSAGE")}
        confirmBtnText={t("CHATBOT.EXIT_CHAT_CONFIRM")}
        cancelBtn={true}
      />
    </Box>
  );
};

// Main component that wraps the content with TypingProvider
const ChatbotComponent = () => {
  return (
    <TypingProvider>
      <ChatbotContent />
    </TypingProvider>
  );
};

export default ChatbotComponent;
